import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Sparkles, X } from 'lucide-react';
import RazorpayButton from '@/components/RazorpayButton';
import OtpVerifyBlock from '@/components/OtpVerifyBlock';
import { extraApi } from '@/lib/extraApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Landing-page booking dialog — amount is admin-editable per capacity.
 *
 * We fetch `/api/subsidy-rates` and use the matching row's `booking_amount`
 * (in INR) as the Razorpay charge. If the API call fails or no matching
 * row is found we fall back to ₹1 (safe minimum, keeps flow alive).
 * The final amount is also shown to the user before they hit "Pay" so there's
 * no surprise at the payment gateway.
 *
 * Phase 36 — Gates the Pay button behind mobile OTP verification when
 * the admin toggle `public_lead_otp_required` is ON. Toggle is fetched
 * lazily when the dialog opens (so admin changes take effect without a
 * full page reload).
 */
export function BookingDialog({ open, onClose, capacity = 3 }) {
    const [f, setF] = useState({ name: '', mobile: '', email: '', city: '' });
    const [rates, setRates] = useState([]);
    const [otpRequired, setOtpRequired] = useState(false);
    const [otpToken, setOtpToken] = useState('');
    const canPay = f.name.trim().length >= 2 && f.mobile.trim().length >= 10 && (!otpRequired || !!otpToken);

    // Lazy-load rates + OTP toggle only when the dialog opens.
    useEffect(() => {
        if (!open) return;
        setOtpToken('');
        (async () => {
            try {
                const { data } = await axios.get(`${API}/subsidy-rates`);
                setRates(Array.isArray(data) ? data : []);
            } catch { setRates([]); }
        })();
        extraApi.publicLeadOtpRequired()
            .then(r => setOtpRequired(!!r?.public_lead_otp_required))
            .catch(() => setOtpRequired(false));
    }, [open]);

    // Find the exact-match row first; if none, fall back to the closest kW.
    const rateRow = (() => {
        if (!rates.length) return null;
        const exact = rates.find(r => Number(r.kw) === Number(capacity));
        if (exact) return exact;
        return rates.slice().sort((a, b) => Math.abs(a.kw - capacity) - Math.abs(b.kw - capacity))[0] || null;
    })();
    const bookingAmountInr = Math.max(1, Number(rateRow?.booking_amount) || 1);
    const bookingAmountPaise = bookingAmountInr * 100;

    const reset = () => { setF({ name: '', mobile: '', email: '', city: '' }); setOtpToken(''); };
    const close = () => { reset(); onClose?.(); };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && close()}>
            <DialogContent className='max-w-md' data-testid='landing-booking-dialog'>
                <DialogHeader>
                    <DialogTitle className='flex items-center gap-2'>
                        <Sparkles className='w-5 h-5 text-accent' />
                        Confirm your Solar Booking
                    </DialogTitle>
                    <p className='text-xs text-muted-foreground mt-1'>
                        <b>₹{bookingAmountInr.toLocaleString('en-IN')}</b> to reserve your slot for ~{capacity} kW system.
                        UPPower Solar's team will call within 24 hours. Fully refundable if you cancel within 24 hrs.
                    </p>
                </DialogHeader>
                <div className='space-y-3 py-2'>
                    <div>
                        <Label>Full Name <span className='text-rose-500'>*</span></Label>
                        <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })}
                            placeholder='Rahul Sharma' data-testid='booking-name' />
                    </div>
                    <div className='grid grid-cols-2 gap-3'>
                        <div>
                            <Label>Mobile <span className='text-rose-500'>*</span></Label>
                            <Input type='tel' inputMode='numeric' value={f.mobile}
                                onChange={e => setF({ ...f, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                placeholder='9876543210' data-testid='booking-mobile' />
                        </div>
                        <div>
                            <Label>City</Label>
                            <Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })}
                                placeholder='Lucknow' data-testid='booking-city' />
                        </div>
                    </div>
                    <div>
                        <Label>Email (optional)</Label>
                        <Input type='email' value={f.email} onChange={e => setF({ ...f, email: e.target.value })}
                            placeholder='rahul@example.com' data-testid='booking-email' />
                    </div>
                    {/* Phase 36 — OTP gate. Renders nothing when toggle is OFF. */}
                    <div className='grid grid-cols-2 gap-3'>
                        <OtpVerifyBlock
                            required={otpRequired}
                            mobile={f.mobile}
                            purpose='book'
                            onVerified={setOtpToken}
                            testId='booking-otp'
                        />
                    </div>
                </div>
                <DialogFooter className='flex-col sm:flex-row gap-2'>
                    <Button variant='outline' onClick={close} data-testid='booking-cancel'>
                        <X className='w-4 h-4 mr-1' /> Cancel
                    </Button>
                    <RazorpayButton
                        flow='booking'
                        amountPaise={bookingAmountPaise}
                        bookingPayload={{
                            name: f.name.trim(),
                            mobile: f.mobile.trim(),
                            email: f.email.trim(),
                            city: f.city.trim(),
                            interest_kw: capacity,
                            otp_token: otpToken || undefined,
                        }}
                        label={`Pay ₹${bookingAmountInr.toLocaleString('en-IN')} & Book Now`}
                        testId='booking-pay-btn'
                        disabled={!canPay}
                        className='bg-accent hover:bg-accent/90 text-accent-foreground'
                        onSuccess={() => { close(); }}
                    />
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BookingDialog;
